import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  CircuitArchive,
  DriverLocation,
  PublicSnapshot,
} from "@paddock-signal/contracts";

import { Icon, type IconName } from "./components/Icon";
import { SignalGlobe, type MapDetailSettings } from "./components/SignalGlobe";
import { circuitSilhouetteAttribution } from "./circuit-silhouettes";
import { filteredDriverIds, type DriverFilters } from "./driver-filters";
import { prototypeSnapshot } from "./data/fixture";
import {
  browserSummary,
  platformSummary,
  readClientDiagnostics,
  recordClientDiagnostic,
} from "./diagnostics";
import {
  LiquidGlassField,
  type MaterialTier,
} from "./material/LiquidGlassField";
import { languageOptions, useI18n, type TranslationKey } from "./i18n";
import {
  formatUserDateTime,
  formatSessionTime,
  nextRaceSession,
  sessionCountdown,
  sessionDisplayStatus,
  type SessionDisplayStatus,
} from "./session-time";
import {
  SnapshotRepository,
  loadCircuitArchive,
  submitFeedback,
  type SnapshotLoadResult,
} from "./snapshot-client";

type Surface =
  | "weekend"
  | "standings"
  | "drivers"
  | "feedback"
  | "info"
  | "circuit";
type PanelState = "closed" | "collapsed" | "expanded";
type Driver = PublicSnapshot["drivers"][number];

const navItems: { icon: IconName; id: Surface; labelKey: TranslationKey }[] = [
  { id: "weekend", labelKey: "nav.weekend", icon: "calendar" },
  { id: "standings", labelKey: "nav.standings", icon: "standings" },
  { id: "drivers", labelKey: "nav.drivers", icon: "drivers" },
  { id: "feedback", labelKey: "nav.feedback", icon: "feedback" },
  { id: "info", labelKey: "nav.about", icon: "info" },
];

const defaultFilters: DriverFilters = {
  confidence: "all",
  constructorId: "all",
  favoritesOnly: false,
  query: "",
};

function BrandMark() {
  return (
    <svg className="brand__mark" viewBox="0 0 112 34" aria-hidden="true">
      <path d="M3 29 17 5h36c10 0 14 4 10 11-3.7 6.5-10.5 9.8-20.5 9.8H20.8L19 29H3Zm24-16-3 5h22c2.7 0 4.4-.9 5.2-2.5.9-1.7-.2-2.5-3.2-2.5H27Z" />
      <path d="M71 5h38l-4.7 8H74.6c-2.4 0-3.8.4-4.3 1.3-.5.9.5 1.3 3 1.3H94c8.8 0 11.4 2.2 7.8 6.7C98.2 26.8 92 29 83 29H43l4.7-8h34.8c2.4 0 3.8-.4 4.3-1.3.5-.8-.5-1.2-3-1.2H63c-8.7 0-11.3-2.3-7.8-6.8C58.8 7.2 64.1 5 71 5Z" />
      <path
        className="brand__mark-cut"
        d="m29 10 13 13h-9L20 10h9Zm49 0 13 13h-8L70 10h8Z"
      />
    </svg>
  );
}

function Confidence({ band }: { band: "high" | "medium" | "low" }) {
  const { t } = useI18n();
  return (
    <span className={`confidence confidence--${band}`}>
      <i aria-hidden="true" />
      {t(`confidence.${band}`)}
    </span>
  );
}

function constructorNames(snapshot: PublicSnapshot): Map<string, string> {
  return new Map(
    snapshot.standings.constructors.map((constructor) => [
      constructor.constructorId,
      constructor.name,
    ]),
  );
}

function DriverIdentity({
  driver,
  teamName,
  compact = false,
}: {
  driver: Driver;
  teamName: string;
  compact?: boolean;
}) {
  return (
    <span
      className={`driver-identity${compact ? " driver-identity--compact" : ""}`}
      style={{ "--driver-accent": driver.accentColor } as React.CSSProperties}
    >
      <span className="driver-identity__motif" aria-hidden="true">
        {driver.acronym.slice(0, 2)}
      </span>
      <span className="driver-identity__name">
        <strong>
          {driver.givenName} {driver.familyName}
        </strong>
        <small>
          {driver.acronym} · #{driver.permanentNumber} · {teamName}
        </small>
      </span>
    </span>
  );
}

function SessionTime({
  startsAt,
  timezone,
}: {
  startsAt: string;
  timezone: string;
}) {
  const time = formatSessionTime(startsAt, timezone);
  return (
    <span>
      <strong>{time.viewer}</strong>
    </span>
  );
}

function sessionStatusLabel(
  status: SessionDisplayStatus,
  t: (key: TranslationKey) => string,
): string {
  if (status === "ongoing") return t("time.ongoing");
  if (status === "next") return t("weekend.nextSession");
  if (status === "scheduled") return t("session.scheduled");
  if (status === "completed") return t("session.completed");
  if (status === "cancelled") return t("session.cancelled");
  return t("session.delayed");
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty-state">
      <span className="empty-state__signal" aria-hidden="true" />
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function WeekendView({
  snapshot,
  now,
}: {
  snapshot: PublicSnapshot;
  now: Date;
}) {
  const { t } = useI18n();
  const weekend = snapshot.weekend;
  if (weekend === null) {
    return (
      <EmptyState
        title={t("weekend.noActive")}
        body={t("weekend.noActiveBody")}
      />
    );
  }
  const circuit = snapshot.circuits.find(
    (candidate) => candidate.id === weekend.circuitId,
  );
  return (
    <div className="view-stack">
      <div className="drawer-heading">
        <span className="eyebrow">
          Round {weekend.round}
          {circuit ? ` · ${circuit.city}, ${circuit.countryCode}` : ""}
        </span>
        <h2>{weekend.name}</h2>
      </div>
      <div className="session-list">
        {weekend.sessions.map((session, index) => (
          <article
            className={`session-row session-row--${sessionDisplayStatus(session, weekend.sessions, now)}`}
            key={session.id}
          >
            <span className="session-row__index">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="session-row__label">
              <strong>{session.label}</strong>
              <small>
                {sessionStatusLabel(
                  sessionDisplayStatus(session, weekend.sessions, now),
                  t,
                )}
              </small>
            </span>
            <SessionTime
              startsAt={session.startsAt}
              timezone={weekend.timezone}
            />
          </article>
        ))}
      </div>
    </div>
  );
}

function CircuitView({
  circuit,
  snapshot,
  now,
  archive,
}: {
  circuit: PublicSnapshot["circuits"][number];
  snapshot: PublicSnapshot;
  now: Date;
  archive: CircuitArchive | null;
}) {
  const { t } = useI18n();
  const weekend =
    snapshot.weekend?.circuitId === circuit.id ? snapshot.weekend : null;
  const resultRounds = (snapshot.progression?.rounds ?? [])
    .filter((round) => round.circuitId === circuit.id)
    .sort((left, right) => right.round - left.round);
  const resultYears = [
    ...new Set([
      ...(resultRounds.length > 0 ? [snapshot.season.year] : []),
      ...(snapshot.sessionResults ?? [])
        .filter((result) => result.circuitId === circuit.id)
        .map((result) => result.season),
      ...(archive?.races ?? [])
        .filter((race) => race.circuitId === circuit.id)
        .map((race) => race.season),
    ]),
  ].sort((left, right) => right - left);
  const [selectedYear, setSelectedYear] = useState(
    resultYears[0] ?? snapshot.season.year,
  );
  useEffect(() => {
    if (resultYears.length === 0 || resultYears.includes(selectedYear)) return;
    setSelectedYear(resultYears[0] ?? snapshot.season.year);
  }, [resultYears, selectedYear, snapshot.season.year]);
  const selectedRounds =
    selectedYear === snapshot.season.year ? resultRounds : [];
  const detailedResults = (snapshot.sessionResults ?? [])
    .filter(
      (result) =>
        result.circuitId === circuit.id && result.season === selectedYear,
    )
    .sort((left, right) => {
      const roundOrder = right.round - left.round;
      return roundOrder !== 0
        ? roundOrder
        : left.kind.localeCompare(right.kind);
    });
  const archiveRaces = (archive?.races ?? [])
    .filter(
      (race) => race.circuitId === circuit.id && race.season === selectedYear,
    )
    .sort((left, right) => right.round - left.round);
  const driverNames = new Map(
    snapshot.drivers.map((driver) => [
      driver.id,
      `${driver.givenName} ${driver.familyName}`,
    ]),
  );
  const driverConstructors = new Map(
    snapshot.drivers.map((driver) => [driver.id, driver.constructorId]),
  );
  const constructorNamesById = constructorNames(snapshot);
  return (
    <div className="view-stack">
      <div className="drawer-heading">
        <span className="eyebrow">
          Round {String(circuit.round).padStart(2, "0")} · {circuit.status}
        </span>
        <h2>{circuit.name}</h2>
        <p>
          {circuit.city}, {circuit.countryCode}
        </p>
      </div>
      <p className={`circuit-status circuit-status--${circuit.status}`}>
        {circuit.status === "past"
          ? t("status.past")
          : circuit.status === "current"
            ? t("status.current")
            : t("status.upcoming")}
      </p>
      {weekend ? (
        <section className="circuit-panel">
          <span className="eyebrow">{t("circuit.thisWeekend")}</span>
          <h3>{weekend.name}</h3>
          <ul className="session-list">
            {weekend.sessions.map((session, index) => (
              <li
                className={`session-row session-row--${sessionDisplayStatus(session, weekend.sessions, now)}`}
                key={session.id}
              >
                <span className="session-row__index">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="session-row__label">
                  <strong>{session.label}</strong>
                  <small>
                    {sessionStatusLabel(
                      sessionDisplayStatus(session, weekend.sessions, now),
                      t,
                    )}
                  </small>
                </span>
                <SessionTime
                  startsAt={session.startsAt}
                  timezone={weekend.timezone}
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {detailedResults.length > 0 ? (
        <section className="circuit-panel circuit-panel--session-results">
          <div className="circuit-panel__header">
            <div>
              <span className="eyebrow">{t("circuit.sessionResults")}</span>
              <h3>{circuit.name}</h3>
            </div>
          </div>
          <div className="session-result-groups">
            {detailedResults.map((result) => (
              <article className="session-result-group" key={result.sessionId}>
                <div className="session-result-group__heading">
                  <strong>{result.label}</strong>
                  <time dateTime={result.completedAt}>
                    {formatUserDateTime(result.completedAt)}
                  </time>
                </div>
                <ol className="session-result-list">
                  {result.entries
                    .slice()
                    .sort(
                      (left, right) =>
                        (left.finishPosition ?? Number.POSITIVE_INFINITY) -
                        (right.finishPosition ?? Number.POSITIVE_INFINITY),
                    )
                    .map((entry) => (
                      <li key={`${result.sessionId}-${entry.driverId}`}>
                        <span className="session-result-list__position">
                          {entry.finishPosition === null
                            ? "—"
                            : String(entry.finishPosition).padStart(2, "0")}
                        </span>
                        <span>
                          <strong>
                            {driverNames.get(entry.driverId) ?? entry.driverId}
                          </strong>
                          <small>
                            {entry.finishTime ??
                              entry.qualifyingTimes?.q3 ??
                              entry.status ??
                              "—"}
                            {entry.interval ? ` · ${entry.interval}` : ""}
                          </small>
                        </span>
                        <b>{entry.points} pts</b>
                      </li>
                    ))}
                </ol>
              </article>
            ))}
          </div>
        </section>
      ) : null}
      {archiveRaces.length > 0 &&
      (selectedYear !== snapshot.season.year ||
        detailedResults.length === 0) ? (
        <section className="circuit-panel circuit-panel--session-results">
          <div className="circuit-panel__header">
            <div>
              <span className="eyebrow">{t("circuit.sessionResults")}</span>
              <h3>{t("circuit.raceResult")}</h3>
            </div>
            <small>{archive?.releaseTag}</small>
          </div>
          <div className="session-result-groups">
            {archiveRaces.map((race) => (
              <article
                className="session-result-group"
                key={`${String(race.season)}-${String(race.round)}`}
              >
                <div className="session-result-group__heading">
                  <strong>{race.raceName}</strong>
                  <time dateTime={race.completedAt}>
                    {formatUserDateTime(race.completedAt)}
                  </time>
                </div>
                {race.sessions.map((session) => (
                  <div
                    key={`${String(race.season)}-${String(race.round)}-${session.kind}`}
                  >
                    <div className="session-result-group__heading">
                      <strong>{session.label}</strong>
                      <time dateTime={session.completedAt}>
                        {formatUserDateTime(session.completedAt)}
                      </time>
                    </div>
                    <ol className="session-result-list">
                      {session.entries.map((entry) => (
                        <li key={`${session.kind}-${entry.driverId}`}>
                          <span className="session-result-list__position">
                            {entry.finishPosition === null
                              ? "—"
                              : String(entry.finishPosition).padStart(2, "0")}
                          </span>
                          <span>
                            <strong>{entry.driverName}</strong>
                            <small>
                              {entry.finishTime ??
                                entry.qualifyingTimes?.q3 ??
                                entry.status ??
                                "—"}
                              {entry.interval ? ` · ${entry.interval}` : ""}
                            </small>
                          </span>
                          <b>{entry.points} pts</b>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </article>
            ))}
          </div>
        </section>
      ) : null}
      <section
        className="circuit-panel circuit-panel--results"
        aria-labelledby="circuit-results-title"
      >
        <div className="circuit-panel__header">
          <div>
            <span className="eyebrow">{t("circuit.publishedResults")}</span>
            <h3 id="circuit-results-title">{t("circuit.raceResult")}</h3>
          </div>
          <label className="circuit-season-picker">
            <span>{t("circuit.season")}</span>
            <select
              aria-label="Results season"
              value={selectedYear}
              onChange={(event) => {
                setSelectedYear(Number(event.target.value));
              }}
            >
              {resultYears.length > 0 ? (
                resultYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))
              ) : (
                <option value={snapshot.season.year}>
                  {snapshot.season.year}
                </option>
              )}
            </select>
          </label>
        </div>
        {selectedRounds.length > 0 ? (
          <div className="circuit-result-rounds">
            {selectedRounds.map((round) => {
              const finishers = round.drivers
                .filter((entry) => entry.finishPosition !== null)
                .sort(
                  (left, right) =>
                    (left.finishPosition ?? Number.POSITIVE_INFINITY) -
                    (right.finishPosition ?? Number.POSITIVE_INFINITY),
                );
              const winner = finishers.at(0);
              return (
                <article className="circuit-result" key={round.round}>
                  <div className="circuit-result__heading">
                    <span>
                      Round {String(round.round).padStart(2, "0")} ·{" "}
                      {round.raceName}
                    </span>
                    <small>{formatUserDateTime(round.completedAt)}</small>
                  </div>
                  {winner ? (
                    <p className="circuit-result__winner">
                      <strong>
                        {driverNames.get(winner.driverId) ?? winner.driverId}
                      </strong>
                      <span>
                        {t("circuit.winner")} · {winner.racePoints}{" "}
                        {t("circuit.points")}
                      </span>
                    </p>
                  ) : null}
                  <ol className="circuit-result__list">
                    {finishers.slice(0, 10).map((entry) => (
                      <li key={entry.driverId}>
                        <span className="circuit-result__position">
                          {String(entry.finishPosition).padStart(2, "0")}
                        </span>
                        <span>
                          <strong>
                            {driverNames.get(entry.driverId) ?? entry.driverId}
                          </strong>
                          <small>
                            {constructorNamesById.get(
                              driverConstructors.get(entry.driverId) ?? "",
                            ) ??
                              driverConstructors.get(entry.driverId) ??
                              t("circuit.constructorNotPublished")}
                          </small>
                        </span>
                        <b>{entry.racePoints} pts</b>
                      </li>
                    ))}
                  </ol>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="circuit-panel__empty">
            {circuit.status === "past"
              ? t("circuit.noPastResult")
              : t("circuit.resultsWillAppear")}
          </p>
        )}
      </section>
    </div>
  );
}

function StandingsView({ snapshot }: { snapshot: PublicSnapshot }) {
  const { t } = useI18n();
  const [table, setTable] = useState<"drivers" | "teams">("drivers");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const names = constructorNames(snapshot);
  const rounds = snapshot.progression?.rounds ?? [];
  const selectedDriver = snapshot.drivers.find(
    (driver) => driver.id === selectedId,
  );
  const selectedTeam = snapshot.standings.constructors.find(
    (constructor) => constructor.constructorId === selectedId,
  );
  if (selectedDriver !== undefined || selectedTeam !== undefined) {
    return (
      <div className="view-stack">
        <button
          className="text-back"
          onClick={() => {
            setSelectedId(null);
          }}
        >
          <Icon name="chevron" /> {t("standings.back")}
        </button>
        <div className="drawer-heading">
          <span className="eyebrow">{t("standings.progression")}</span>
          <h2>
            {selectedDriver
              ? `${selectedDriver.givenName} ${selectedDriver.familyName}`
              : selectedTeam?.name}
          </h2>
          <p>{t("standings.progressionDescription")}</p>
        </div>
        {rounds.length === 0 ? (
          <EmptyState
            title={t("standings.notPublished")}
            body={t("standings.notPublishedBody")}
          />
        ) : (
          <ol className="progression-list">
            {rounds.map((round) => {
              const result = selectedDriver
                ? round.drivers.find(
                    (entry) => entry.driverId === selectedDriver.id,
                  )
                : round.constructors.find(
                    (entry) =>
                      entry.constructorId === selectedTeam?.constructorId,
                  );
              if (result === undefined) return null;
              const weekendPoints = result.racePoints + result.sprintPoints;
              return (
                <li key={`${String(round.round)}-${selectedId ?? "entry"}`}>
                  <span className="progression-list__round">
                    R{String(round.round).padStart(2, "0")}
                  </span>
                  <span>
                    <strong>{round.raceName}</strong>
                    <small>
                      {"finishPosition" in result && result.finishPosition
                        ? `${t("standings.racePosition")} P${String(result.finishPosition)} · `
                        : ""}
                      +{weekendPoints} pts
                    </small>
                  </span>
                  <span className="progression-list__total">
                    <strong>{result.cumulativePoints}</strong>
                    <small>
                      P{result.championshipPosition} {t("standings.afterRound")}
                    </small>
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    );
  }
  return (
    <div className="view-stack">
      <div className="drawer-heading">
        <span className="eyebrow">{t("standings.latest")}</span>
        <h2>{t("standings.title")}</h2>
        <p>{formatUserDateTime(snapshot.standings.publishedAt)}</p>
      </div>
      <div className="segment" aria-label={t("standings.table")}>
        <button
          aria-pressed={table === "drivers"}
          onClick={() => {
            setTable("drivers");
            setSelectedId(null);
          }}
        >
          {t("standings.drivers")}
        </button>
        <button
          aria-pressed={table === "teams"}
          onClick={() => {
            setTable("teams");
            setSelectedId(null);
          }}
        >
          {t("standings.teams")}
        </button>
      </div>
      <ol className="standings-list">
        {table === "drivers"
          ? snapshot.standings.drivers.map((standing) => {
              const driver = snapshot.drivers.find(
                (item) => item.id === standing.driverId,
              );
              if (driver === undefined) return null;
              return (
                <li key={standing.driverId}>
                  <button
                    className="standing-open"
                    aria-label={`Open ${driver.givenName} ${driver.familyName} season progression`}
                    onClick={() => {
                      setSelectedId(driver.id);
                    }}
                  >
                    <span className="standing-position">
                      {String(standing.position).padStart(2, "0")}
                    </span>
                    <DriverIdentity
                      driver={driver}
                      teamName={
                        names.get(driver.constructorId) ?? driver.constructorId
                      }
                      compact
                    />
                    <strong className="standing-points">
                      {standing.points}
                      <small>PTS</small>
                    </strong>
                  </button>
                </li>
              );
            })
          : snapshot.standings.constructors.map((standing) => (
              <li key={standing.constructorId}>
                <button
                  className="standing-open"
                  aria-label={`Open ${standing.name} season progression`}
                  onClick={() => {
                    setSelectedId(standing.constructorId);
                  }}
                >
                  <span className="standing-position">
                    {String(standing.position).padStart(2, "0")}
                  </span>
                  <span className="team-name">
                    <i style={{ background: standing.accentColor }} />
                    {standing.name}
                  </span>
                  <strong className="standing-points">
                    {standing.points}
                    <small>PTS</small>
                  </strong>
                </button>
              </li>
            ))}
      </ol>
    </div>
  );
}

interface DriversViewProps {
  favorites: Set<string>;
  filters: DriverFilters;
  onChangeFilters: (filters: DriverFilters) => void;
  onSelect: (driverId: string) => void;
  onToggleFavorite: (driverId: string) => void;
  snapshot: PublicSnapshot;
  visibleDriverIds: readonly string[];
}

function DriversView({
  favorites,
  filters,
  onChangeFilters,
  onSelect,
  onToggleFavorite,
  snapshot,
  visibleDriverIds,
}: DriversViewProps) {
  const { t } = useI18n();
  const names = constructorNames(snapshot);
  const visible = new Set(visibleDriverIds);
  const results = snapshot.drivers.filter(
    (driver) => driver.active && visible.has(driver.id),
  );
  return (
    <div className="view-stack">
      <div className="drawer-heading">
        <span className="eyebrow">{t("drivers.fullGrid")}</span>
        <h2>{t("drivers.find")}</h2>
        <p>{t("drivers.searchDescription")}</p>
      </div>
      <label className="search-field">
        <Icon name="search" />
        <span className="sr-only">{t("driver.search")}</span>
        <input
          value={filters.query}
          onChange={(event) => {
            onChangeFilters({ ...filters, query: event.target.value });
          }}
          placeholder={t("drivers.searchPlaceholder")}
        />
      </label>
      <div className="filter-row">
        <button
          className={`filter-chip${filters.favoritesOnly ? " is-active" : ""}`}
          onClick={() => {
            onChangeFilters({
              ...filters,
              favoritesOnly: !filters.favoritesOnly,
            });
          }}
        >
          <Icon name="star" />
          {t("drivers.favorites")}{" "}
          {favorites.size > 0 && <span>{favorites.size}</span>}
        </button>
        <label className="filter-select">
          <span>{t("drivers.team")}</span>
          <select
            aria-label="Filter by team"
            value={filters.constructorId}
            onChange={(event) => {
              onChangeFilters({
                ...filters,
                constructorId: event.target.value,
              });
            }}
          >
            <option value="all">{t("drivers.allTeams")}</option>
            {snapshot.standings.constructors.map((constructor) => (
              <option
                value={constructor.constructorId}
                key={constructor.constructorId}
              >
                {constructor.name}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-select">
          <span>{t("drivers.signal")}</span>
          <select
            aria-label="Filter by confidence"
            value={filters.confidence}
            onChange={(event) => {
              onChangeFilters({
                ...filters,
                confidence: event.target.value as DriverFilters["confidence"],
              });
            }}
          >
            <option value="all">{t("drivers.allSignals")}</option>
            <option value="high">{t("confidence.high")}</option>
            <option value="medium">{t("confidence.medium")}</option>
            <option value="low">{t("confidence.low")}</option>
            <option value="unknown">{t("confidence.unknown")}</option>
          </select>
        </label>
      </div>
      {results.length === 0 ? (
        <EmptyState
          title={t("common.noMatchingDrivers")}
          body={t("common.clearFilters")}
        />
      ) : (
        <ul className="driver-list">
          {results.map((driver) => {
            const location = snapshot.locations.find(
              (item) => item.driverId === driver.id,
            );
            return (
              <li key={driver.id}>
                <button
                  className="driver-list__open"
                  onClick={() => {
                    onSelect(driver.id);
                  }}
                >
                  <DriverIdentity
                    driver={driver}
                    teamName={
                      names.get(driver.constructorId) ?? driver.constructorId
                    }
                    compact
                  />
                  <span className="driver-list__location">
                    {location?.status === "estimated" ? (
                      <>
                        {location.city}
                        <Confidence band={location.confidenceBand} />
                      </>
                    ) : (
                      <>
                        {t("driver.noEstimate")}
                        <small>{t("driver.activeGrid")}</small>
                      </>
                    )}
                  </span>
                </button>
                <button
                  className="favorite-button"
                  style={
                    {
                      "--favorite-accent": driver.accentColor,
                    } as React.CSSProperties
                  }
                  aria-label={`${favorites.has(driver.id) ? "Remove" : "Add"} ${driver.givenName} ${driver.familyName} ${favorites.has(driver.id) ? "from" : "to"} favorites`}
                  aria-pressed={favorites.has(driver.id)}
                  onClick={() => {
                    onToggleFavorite(driver.id);
                  }}
                >
                  <Icon name="star" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function evidenceAge(observedAt: string, generatedAt: string): string {
  const hours = Math.max(
    0,
    Math.floor((Date.parse(generatedAt) - Date.parse(observedAt)) / 3_600_000),
  );
  return hours === 0 ? "under 1h" : `${String(hours)}h`;
}

function DriverView({
  driver,
  favorite,
  location,
  onToggleFavorite,
  snapshot,
}: {
  driver: Driver;
  favorite: boolean;
  location: DriverLocation | undefined;
  onToggleFavorite: () => void;
  snapshot: PublicSnapshot;
}) {
  const { t } = useI18n();
  const names = constructorNames(snapshot);
  return (
    <div className="view-stack">
      <div className="driver-profile">
        <DriverIdentity
          driver={driver}
          teamName={names.get(driver.constructorId) ?? driver.constructorId}
        />
        <button
          className="favorite-button favorite-button--large"
          style={
            { "--favorite-accent": driver.accentColor } as React.CSSProperties
          }
          aria-label={`${favorite ? "Remove from" : "Add to"} favorites`}
          aria-pressed={favorite}
          onClick={onToggleFavorite}
        >
          <Icon name="star" />
        </button>
      </div>
      {location?.status === "estimated" ? (
        <>
          <div className="location-hero">
            <span className="location-hero__city">{location.city}</span>
            <span className="location-hero__country">
              {location.countryCode}
            </span>
            <Confidence band={location.confidenceBand} />
            <p>{location.rationale}</p>
          </div>
          <section className="evidence-section">
            <span className="eyebrow">{t("driver.whyHere")}</span>
            {location.evidence.map((evidence) => (
              <a
                className="evidence-card"
                href={evidence.url}
                key={evidence.id}
                target="_blank"
                rel="noreferrer"
              >
                <span>
                  <strong>{evidence.publisher}</strong>
                  <small>{evidence.title}</small>
                </span>
                <span className="evidence-card__meta">
                  {evidence.freshness} ·{" "}
                  {evidenceAge(evidence.observedAt, snapshot.generatedAt)}
                  <Icon name="chevron" />
                </span>
              </a>
            ))}
          </section>
          {location.expectedNext && (
            <section className="next-stop">
              <span className="eyebrow">{t("driver.expectedNext")}</span>
              <h3>{location.expectedNext.city}</h3>
              <p>{location.expectedNext.label}</p>
              <time dateTime={location.expectedNext.startsAt}>
                {formatUserDateTime(location.expectedNext.startsAt)}
              </time>
            </section>
          )}
        </>
      ) : (
        <EmptyState
          title={t("common.noReliableEstimate")}
          body={t("common.noReliableEstimateBody")}
        />
      )}
    </div>
  );
}

function InfoView({
  materialTier,
  snapshotResult,
}: {
  materialTier: MaterialTier;
  snapshotResult: SnapshotLoadResult;
}) {
  const { t } = useI18n();
  const healthy = snapshotResult.snapshot.sourceHealth.filter(
    (source) => source.status === "healthy",
  ).length;
  return (
    <div className="view-stack">
      <div className="drawer-heading">
        <span className="eyebrow">{t("about.eyebrow")}</span>
        <h2>{t("about.heading")}</h2>
        <p>{t("about.description")}</p>
      </div>
      <dl className="system-status">
        <div>
          <dt>{t("info.snapshot")}</dt>
          <dd>
            {snapshotResult.state} · {snapshotResult.source}
          </dd>
        </div>
        <div>
          <dt>{t("info.sources")}</dt>
          <dd>
            {healthy} {t("info.healthy")}
          </dd>
        </div>
        <div>
          <dt>{t("info.material")}</dt>
          <dd>{materialTier}</dd>
        </div>
      </dl>
      <p className="legal-copy">
        This is an unofficial site and is not associated with Formula 1, the
        FIA, any team, driver, or championship organizer. Fixture mode uses
        synthetic names and data.
      </p>
      <p className="legal-copy">
        City-level location estimates and their supporting evidence are kept for
        at most 30 days, then purged. To request a correction or a takedown of a
        specific estimate, use the Feedback tab. It is routed to the project
        owner and is never published as a public post. Suppression and takedown
        requests override any source or automated evaluation.
      </p>
      <p className="attribution">
        Map data: <a href="https://openfreemap.org/">OpenFreeMap</a> ·{" "}
        <a href="https://openmaptiles.org/">© OpenMapTiles</a> ·{" "}
        <a href="https://www.openstreetmap.org/copyright">
          OpenStreetMap contributors
        </a>
        {" · "}
        <a href="https://github.com/julesr0y/f1-circuits-svg">
          {circuitSilhouetteAttribution}
        </a>
      </p>
    </div>
  );
}

function FeedbackView({
  materialTier,
  snapshotResult,
  apiOrigin,
}: {
  materialTier: MaterialTier;
  snapshotResult: SnapshotLoadResult;
  apiOrigin?: string;
}) {
  const { t } = useI18n();
  const [category, setCategory] = useState<"issue" | "error" | "idea">("idea");
  const [details, setDetails] = useState("");
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [submission, setSubmission] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");
  const diagnosticLines = [
    `App: Paddock Signal schema ${String(snapshotResult.snapshot.schemaVersion)}`,
    `Snapshot: ${snapshotResult.snapshot.id} (${snapshotResult.state}/${snapshotResult.source})`,
    `Viewport: ${String(window.innerWidth)}×${String(window.innerHeight)} @ ${String(window.devicePixelRatio)}x`,
    `Platform: ${platformSummary(navigator.userAgent)}`,
    `Browser: ${browserSummary(navigator.userAgent)}`,
    `User locale/time zone: ${navigator.language} / ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    `Material: ${materialTier}; reduced motion: ${String(window.matchMedia("(prefers-reduced-motion: reduce)").matches)}`,
    `App errors: ${
      readClientDiagnostics()
        .map((item) => item.code)
        .join(", ") || "none captured"
    }`,
  ];
  const sendFeedback = async (): Promise<void> => {
    setSubmission("sending");
    try {
      await submitFeedback(apiOrigin, {
        category,
        details: details.trim(),
        ...(includeDiagnostics
          ? { diagnostics: diagnosticLines.join("\n") }
          : {}),
        snapshotId: snapshotResult.snapshot.id,
      });
      setSubmission("sent");
    } catch {
      recordClientDiagnostic("feedback-submit-failed");
      setSubmission("error");
    }
  };

  return (
    <form
      className="view-stack feedback-form"
      onSubmit={(event) => {
        event.preventDefault();
        void sendFeedback();
      }}
    >
      <div className="drawer-heading">
        <span className="eyebrow">{t("feedback.eyebrow")}</span>
        <h2>{t("feedback.heading")}</h2>
        <p>{t("feedback.description")}</p>
      </div>
      <label className="feedback-field">
        <span>{t("feedback.category")}</span>
        <select
          value={category}
          onChange={(event) => {
            setCategory(event.target.value as typeof category);
          }}
        >
          <option value="idea">{t("feedback.idea")}</option>
          <option value="issue">{t("feedback.issue")}</option>
          <option value="error">{t("feedback.error")}</option>
        </select>
      </label>
      <label className="feedback-field">
        <span>{t("feedback.whatChanged")}</span>
        <textarea
          required
          maxLength={2_000}
          value={details}
          onChange={(event) => {
            setDetails(event.target.value);
            setSubmission("idle");
          }}
          placeholder={t("feedback.placeholder")}
        />
      </label>
      <label className="diagnostic-consent">
        <input
          type="checkbox"
          checked={includeDiagnostics}
          onChange={(event) => {
            setIncludeDiagnostics(event.target.checked);
          }}
        />
        <span>{t("feedback.includeDiagnostics")}</span>
      </label>
      {includeDiagnostics && (
        <pre className="diagnostic-preview">{diagnosticLines.join("\n")}</pre>
      )}
      <div className="feedback-actions">
        <button
          className="liquid-button"
          type="submit"
          disabled={submission === "sending" || details.trim().length === 0}
        >
          {submission === "sending"
            ? t("action.sending")
            : submission === "sent"
              ? t("action.feedbackSent")
              : t("action.sendFeedback")}
        </button>
      </div>
      {submission === "error" ? (
        <p className="feedback-error" role="alert">
          {t("feedback.errorMessage")}
        </p>
      ) : null}
      <p className="feedback-privacy">
        Feedback excludes browsing history, precise location, source text,
        secrets, and arbitrary console output. Messages are kept in the
        project's Cloudflare inbox for up to 30 days, then purged. They are
        visible only to the project owner and are never published as a post.
      </p>
    </form>
  );
}

function storedFavorites(): Set<string> {
  try {
    const parsed = JSON.parse(
      localStorage.getItem("ps-favorites") ?? "[]",
    ) as unknown;
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter((value): value is string => typeof value === "string")
        : [],
    );
  } catch {
    return new Set();
  }
}

function snapshotStorage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export default function App() {
  const { language, setLanguage, t } = useI18n();
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const selectedLanguage =
    languageOptions.find((option) => option.code === language) ??
    languageOptions[0];
  const queryParameters = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const staleMode = queryParameters.get("stale") === "true";
  const remoteSnapshot =
    queryParameters.get("snapshot") === "remote" ||
    (!import.meta.env.DEV && queryParameters.get("snapshot") !== "fixture");
  const publicApiOrigin = import.meta.env.VITE_PUBLIC_API_ORIGIN;
  const repositoryRef = useRef<SnapshotRepository | null>(null);
  repositoryRef.current ??= new SnapshotRepository({
    ...(publicApiOrigin === undefined ? {} : { apiOrigin: publicApiOrigin }),
    fallback: prototypeSnapshot,
    storage: snapshotStorage(),
  });
  const [snapshotResult, setSnapshotResult] = useState<SnapshotLoadResult>(
    () =>
      repositoryRef.current?.fixture() ?? {
        snapshot: prototypeSnapshot,
        source: "fixture",
        state: "fresh",
      },
  );
  const snapshot = snapshotResult.snapshot;
  const [circuitArchive, setCircuitArchive] = useState<CircuitArchive | null>(
    null,
  );
  /**
   * remoteSnapshot means this load was meant to reach a real snapshot
   * source. If it never has — source is still "fixture" even though we
   * asked for real data — the bundled sample content must not be shown as
   * if it were live: no fake drivers, no fake weekend/standings, no fake
   * circuits. Explicit ?snapshot=fixture (used by local dev and every
   * Playwright test) is a separate, honestly-labelled demo mode and keeps
   * showing the sample content normally.
   */
  const liveDataUnavailable =
    remoteSnapshot && snapshotResult.source === "fixture";
  const [surface, setSurface] = useState<Surface>("weekend");
  const [panelState, setPanelState] = useState<PanelState>("closed");
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [selectedCircuitId, setSelectedCircuitId] = useState<string | null>(
    null,
  );
  const [selectedGroup, setSelectedGroup] = useState<string[]>([]);
  const [materialTier, setMaterialTier] = useState<MaterialTier>("opaque");
  const [favorites, setFavorites] = useState<Set<string>>(storedFavorites);
  const [filters, setFilters] = useState<DriverFilters>(defaultFilters);
  const [mapControlsOpen, setMapControlsOpen] = useState(false);
  const detailShellScrollRef = useRef<HTMLDivElement>(null);
  const [clockNow, setClockNow] = useState(() => new Date());
  const [mapDetails, setMapDetails] = useState<MapDetailSettings>({
    countryLabels: false,
    circuits: true,
  });

  useEffect(() => {
    const timer = window.setInterval(() => {
      setClockNow(new Date());
    }, 1_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);
  const visibleDriverIds = useMemo(
    () => filteredDriverIds(snapshot, filters, favorites),
    [favorites, filters, snapshot],
  );
  const visibleDriverIdSet = useMemo(
    () => new Set(visibleDriverIds),
    [visibleDriverIds],
  );
  const selectedDriver = snapshot.drivers.find(
    (driver) => driver.id === selectedDriverId,
  );
  const selectedLocation = snapshot.locations.find(
    (location) => location.driverId === selectedDriverId,
  );
  const selectedCircuit = snapshot.circuits.find(
    (circuit) => circuit.id === selectedCircuitId,
  );
  const activeNavigationIndex = navItems.findIndex(
    (item) => item.id === surface,
  );

  const [refreshingSnapshot, setRefreshingSnapshot] = useState(false);
  const refreshSnapshot = useCallback(async (): Promise<void> => {
    setRefreshingSnapshot(true);
    const result = await repositoryRef.current?.load();
    setRefreshingSnapshot(false);
    if (result === undefined) return;
    if (result.message !== undefined) {
      recordClientDiagnostic("snapshot-refresh-fallback");
    }
    setSnapshotResult(result);
  }, []);

  useEffect(() => {
    if (!remoteSnapshot) return;
    void refreshSnapshot();
  }, [refreshSnapshot, remoteSnapshot]);

  useEffect(() => {
    if (
      !remoteSnapshot ||
      selectedCircuitId === null ||
      publicApiOrigin === undefined
    ) {
      setCircuitArchive(null);
      return;
    }
    let cancelled = false;
    setCircuitArchive(null);
    void loadCircuitArchive(publicApiOrigin, selectedCircuitId)
      .then((archive) => {
        if (!cancelled) setCircuitArchive(archive);
      })
      .catch(() => {
        if (!cancelled) setCircuitArchive(null);
      });
    return () => {
      cancelled = true;
    };
  }, [publicApiOrigin, remoteSnapshot, selectedCircuitId]);

  useEffect(() => {
    try {
      localStorage.setItem("ps-favorites", JSON.stringify([...favorites]));
    } catch {
      return;
    }
  }, [favorites]);

  const openDriver = useCallback((driverId: string) => {
    setSelectedDriverId(driverId);
    setSelectedCircuitId(null);
    setSelectedGroup([]);
    setSurface("drivers");
    setPanelState("expanded");
  }, []);
  const openGroup = useCallback((driverIds: string[]) => {
    setSelectedGroup(driverIds);
    setSelectedDriverId(null);
    setSelectedCircuitId(null);
    setSurface("drivers");
    setPanelState("expanded");
  }, []);
  const openCircuit = useCallback((circuitId: string) => {
    setSelectedCircuitId(circuitId);
    setSelectedDriverId(null);
    setSelectedGroup([]);
    setSurface("circuit");
    setPanelState("collapsed");
  }, []);
  const toggleFavorite = useCallback((driverId: string) => {
    setFavorites((current) => {
      const next = new Set(current);
      if (next.has(driverId)) next.delete(driverId);
      else next.add(driverId);
      return next;
    });
  }, []);
  const chooseSurface = (next: Surface) => {
    detailShellScrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
    setSurface(next);
    setSelectedDriverId(null);
    setSelectedGroup([]);
    setSelectedCircuitId(null);
    setPanelState("expanded");
  };

  const content = useMemo(() => {
    if (selectedCircuit !== undefined) {
      return (
        <CircuitView
          circuit={selectedCircuit}
          snapshot={snapshot}
          now={clockNow}
          archive={circuitArchive}
        />
      );
    }
    if (selectedDriver !== undefined) {
      return (
        <DriverView
          driver={selectedDriver}
          location={selectedLocation}
          favorite={favorites.has(selectedDriver.id)}
          onToggleFavorite={() => {
            toggleFavorite(selectedDriver.id);
          }}
          snapshot={snapshot}
        />
      );
    }
    if (selectedGroup.length > 0) {
      const names = constructorNames(snapshot);
      return (
        <div className="view-stack">
          <div className="drawer-heading">
            <span className="eyebrow">Driver cluster</span>
            <h2>{selectedGroup.length} nearby drivers</h2>
          </div>
          <div className="cluster-list">
            {selectedGroup.map((id) => {
              const driver = snapshot.drivers.find((item) => item.id === id);
              return driver ? (
                <button
                  key={id}
                  onClick={() => {
                    openDriver(id);
                  }}
                >
                  <DriverIdentity
                    driver={driver}
                    teamName={
                      names.get(driver.constructorId) ?? driver.constructorId
                    }
                    compact
                  />
                  <Icon name="chevron" />
                </button>
              ) : null;
            })}
          </div>
        </div>
      );
    }
    if (
      surface === "weekend" ||
      surface === "standings" ||
      surface === "drivers"
    ) {
      if (liveDataUnavailable) {
        return (
          <EmptyState
            title="Live signal not connected"
            body="This preview has no real snapshot source configured, so it shows nothing rather than sample data standing in for it."
          />
        );
      }
      if (surface === "weekend") {
        return <WeekendView snapshot={snapshot} now={clockNow} />;
      }
      if (surface === "standings") return <StandingsView snapshot={snapshot} />;
      return (
        <DriversView
          favorites={favorites}
          filters={filters}
          onChangeFilters={setFilters}
          onSelect={openDriver}
          onToggleFavorite={toggleFavorite}
          snapshot={snapshot}
          visibleDriverIds={visibleDriverIds}
        />
      );
    }
    if (surface === "info") {
      return (
        <InfoView materialTier={materialTier} snapshotResult={snapshotResult} />
      );
    }
    return (
      <FeedbackView
        apiOrigin={publicApiOrigin}
        materialTier={materialTier}
        snapshotResult={snapshotResult}
      />
    );
  }, [
    favorites,
    filters,
    liveDataUnavailable,
    materialTier,
    openDriver,
    publicApiOrigin,
    selectedDriver,
    selectedCircuit,
    selectedCircuitId,
    circuitArchive,
    selectedGroup,
    selectedLocation,
    snapshot,
    snapshotResult,
    surface,
    toggleFavorite,
    visibleDriverIds,
    clockNow,
  ]);
  const contentKey =
    selectedCircuitId ??
    selectedDriverId ??
    (selectedGroup.length > 0 ? `group-${selectedGroup.join("-")}` : surface);
  const nextSession =
    !liveDataUnavailable && snapshot.weekend
      ? nextRaceSession(snapshot.weekend.sessions, clockNow)
      : undefined;
  const nextSessionTime =
    nextSession && snapshot.weekend
      ? formatSessionTime(nextSession.startsAt, snapshot.weekend.timezone)
      : null;
  const nextSessionCountdown = nextSession
    ? sessionCountdown(nextSession, clockNow)
    : null;
  const snapshotStale = staleMode || snapshotResult.state === "stale";

  useEffect(() => {
    const scroll = detailShellScrollRef.current;
    if (scroll !== null) scroll.scrollTop = 0;
  }, [contentKey, panelState]);

  useEffect(() => {
    const dismissOverlays = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest(
          ".detail-shell, .dock, .next-session, .topbar, .stale-banner, .map-controls, .circuit-marker, .driver-marker, .driver-cluster, .maplibregl-marker",
        ) !== null
      ) {
        return;
      }
      setMapControlsOpen(false);
      setPanelState("closed");
    };
    document.addEventListener("click", dismissOverlays);
    return () => {
      document.removeEventListener("click", dismissOverlays);
    };
  }, []);

  return (
    <main
      className="app-shell"
      data-live-data-unavailable={liveDataUnavailable}
      data-material-tier={materialTier}
      data-panel-state={panelState}
      data-snapshot-source={snapshotResult.source}
      data-snapshot-state={snapshotStale ? "stale" : "fresh"}
      data-surface={surface}
    >
      <a className="skip-link" href="#signal-content">
        Skip to signal details
      </a>
      <SignalGlobe
        circuits={liveDataUnavailable ? [] : snapshot.circuits}
        drivers={liveDataUnavailable ? [] : snapshot.drivers}
        locations={liveDataUnavailable ? [] : snapshot.locations}
        mapDetails={mapDetails}
        selectedDriverId={selectedDriverId}
        visibleDriverIds={visibleDriverIdSet}
        onSelectDriver={openDriver}
        onSelectGroup={openGroup}
        onSelectCircuit={openCircuit}
      />
      <LiquidGlassField onTierChange={setMaterialTier} />
      <header className="topbar">
        <button
          className="brand"
          aria-label={t("brand.reset")}
          onClick={() => {
            setPanelState("closed");
            window.dispatchEvent(new Event("ps:reset-map"));
          }}
        >
          <BrandMark />
          <span>
            <strong>Paddock Signal</strong>
          </span>
        </button>
        <div className="language-picker">
          <button
            className="language-picker__trigger glass-control"
            type="button"
            aria-label={`${t("language.select")}: ${selectedLanguage.name}`}
            aria-expanded={languageMenuOpen}
            aria-haspopup="listbox"
            onClick={() => {
              setLanguageMenuOpen((current) => !current);
            }}
          >
            <span aria-hidden="true">{selectedLanguage.flag}</span>
            <span className="sr-only">{selectedLanguage.name}</span>
          </button>
          {languageMenuOpen && (
            <div
              className="language-picker__menu glass-surface"
              role="listbox"
              aria-label={t("language.select")}
            >
              {languageOptions.map((option) => (
                <button
                  className="language-picker__option"
                  type="button"
                  role="option"
                  aria-selected={option.code === language}
                  aria-label={option.name}
                  title={option.name}
                  key={option.code}
                  onClick={() => {
                    setLanguage(option.code);
                    setLanguageMenuOpen(false);
                  }}
                >
                  <span aria-hidden="true">{option.flag}</span>
                  <span className="sr-only">{option.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </header>
      {liveDataUnavailable ? (
        <div className="stale-banner stale-banner--unavailable" role="status">
          <span>{t("status.liveSignalUnavailable")}</span>
          {snapshotResult.message ?? t("status.noSnapshot")}
          <button
            disabled={refreshingSnapshot}
            onClick={() => {
              void refreshSnapshot();
            }}
          >
            {refreshingSnapshot ? t("action.sending") : t("action.tryAgain")}
          </button>
        </div>
      ) : null}
      {nextSession && nextSessionTime && snapshot.weekend && (
        <button
          className="next-session glass-surface"
          onClick={() => {
            chooseSurface("weekend");
          }}
        >
          <span className="next-session__round">R{snapshot.weekend.round}</span>
          <span>
            <small>
              {sessionStatusLabel(
                sessionDisplayStatus(
                  nextSession,
                  snapshot.weekend.sessions,
                  clockNow,
                ),
                t,
              )}
            </small>
            <strong>{nextSession.label}</strong>
          </span>
          <span className="next-session__time">
            <strong className="next-session__countdown">
              {nextSessionCountdown}
            </strong>
            <small>{nextSessionTime.viewerClock}</small>
          </span>
          <Icon name="chevron" />
        </button>
      )}
      <aside
        id="signal-content"
        className="detail-shell glass-surface"
        aria-label={t("a11y.signalDetails")}
        aria-hidden={panelState === "closed"}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <button
          className="drawer-close glass-control"
          aria-label={t("action.close")}
          onClick={() => {
            setPanelState("closed");
          }}
        >
          <Icon name="close" />
        </button>
        <button
          className="drawer-toggle glass-control"
          aria-label={
            panelState === "collapsed"
              ? t("action.expand")
              : t("action.collapse")
          }
          aria-expanded={panelState === "expanded"}
          disabled={panelState === "closed"}
          onClick={() => {
            setPanelState((current) =>
              current === "collapsed" ? "expanded" : "collapsed",
            );
          }}
        >
          <Icon name={panelState === "collapsed" ? "expand" : "collapse"} />
        </button>
        <div
          className="detail-shell__scroll"
          ref={detailShellScrollRef}
          inert={panelState === "closed" ? true : undefined}
          tabIndex={panelState === "closed" ? -1 : 0}
        >
          <div className="detail-transition" key={contentKey}>
            {content}
          </div>
        </div>
      </aside>
      <nav
        className="dock glass-surface"
        aria-label="Primary"
        style={
          { "--active-index": activeNavigationIndex } as React.CSSProperties
        }
      >
        <span className="dock__liquid-selection" aria-hidden="true" />
        {navItems.map((item) => (
          <button
            key={item.id}
            aria-current={
              panelState !== "closed" &&
              surface === item.id &&
              selectedDriverId === null &&
              selectedGroup.length === 0 &&
              selectedCircuitId === null
                ? "page"
                : undefined
            }
            onClick={() => {
              chooseSurface(item.id);
            }}
          >
            <span className="dock__icon">
              <Icon name={item.icon} />
            </span>
            <span>{t(item.labelKey)}</span>
          </button>
        ))}
      </nav>
      <div className="map-controls">
        {mapControlsOpen && (
          <div
            className="map-options glass-surface"
            aria-label={t("map.display")}
          >
            <label>
              <input
                type="checkbox"
                checked={mapDetails.countryLabels}
                onChange={(event) => {
                  setMapDetails((current) => ({
                    ...current,
                    countryLabels: event.target.checked,
                  }));
                }}
              />
              {t("map.countryLabels")}
            </label>
            <label>
              <input
                type="checkbox"
                checked={mapDetails.circuits}
                onChange={(event) => {
                  setMapDetails((current) => ({
                    ...current,
                    circuits: event.target.checked,
                  }));
                }}
              />
              {t("map.circuits")}
            </label>
          </div>
        )}
        <button
          className="map-control glass-control"
          aria-label={t("map.displayOptions")}
          aria-expanded={mapControlsOpen}
          onClick={() => {
            setMapControlsOpen((current) => !current);
          }}
        >
          <Icon name="layers" />
        </button>
        <button
          className="map-control glass-control"
          aria-label={t("map.reset")}
          onClick={() => window.dispatchEvent(new Event("ps:reset-map"))}
        >
          <Icon name="compass" />
        </button>
      </div>
      <div
        className="screen-reader-roster sr-only"
        aria-label={t("a11y.driverRoster")}
      >
        <h2>{t("a11y.activeRoster")}</h2>
        <ul>
          {snapshot.drivers
            .filter((driver) => driver.active)
            .map((driver) => (
              <li key={driver.id} data-driver-id={driver.id}>
                <button
                  onClick={() => {
                    openDriver(driver.id);
                  }}
                >
                  {driver.givenName} {driver.familyName}
                </button>
              </li>
            ))}
        </ul>
      </div>
    </main>
  );
}
